// @flow
import gql from 'graphql-tag';
import React, {Component} from 'react';
import classnames from 'classnames';
import HitCarouselContainer from '../HitCarouselContainer/HitCarouselContainer';
import {
    SleepsElement,
    BedroomsPropertyTypeElement,
    BathroomsElement
} from '../Details/Details';
import {fragments as hitFragments} from '../Hit/Hit';
import {filter as filterGraphQlAnywhere} from 'graphql-anywhere';
import Price, {fragments as priceFragments} from '../Price/Price';
import Rating, {fragments as ratingFragments} from '../Rating/Rating';
import {MAP_PHOTOCAROUSEL_ADVANCE} from '../../utils/carouselEdapUtils';
import TripBoardButton from '../TripBoardButton/TripBoardButton';

import type {
    SERPHitShape as HitSERPHitShape,
    ListingShape as HitListingShape
} from '../Hit/Hit';

import './MapHit.less';
export const fragments = {
    SERPHit: gql`
        fragment MapHitSERPHit on SERPHit {
            location {
                latitude
                longitude
            }
        }
    `,
    listing: gql`
        fragment MapHitListing on Listing {
            ...HitListing
        }
        ${hitFragments.listing}
    `
};

export type SERPHitShape = {
    location?: {
        latitude: string,
        longitude: string
    },
    fullLocale: string
} & HitSERPHitShape;

export type ListingShape = HitListingShape;

type Props = {
    hit: SERPHitShape,
    listing: ListingShape,
    // TODO: fix onClick here.
    onHitClick?: (hit: SERPHitShape, listing: ListingShape) => void
};

type State = {
    isLoading: boolean
};

/**
 * Typically rendered within a google maps infobox, which stops propagation on all
 * events. Sets up a ref on the main hit anchor element. No other DOM events
 * will work unless you create refs and wire them up manually.
 */
export default class MapHit extends Component<Props, State> {
    constructor() {
        super();
        this.state = {
            isLoading: true
        };
    }

    componentDidMount() {
        /* istanbul ignore else */
        if (this.linkRef.current) {
            this.linkRef.current.addEventListener('click', this.onClick);
        }
    }

    componentWillUnmount() {
        /* istanbul ignore else */
        if (this.linkRef.current) {
            this.linkRef.current.removeEventListener('click', this.onClick);
        }
    }

    // TODO: should be able to type this with something like `Ref<HTMLAnchorElement>`,
    // but Flow 0.74 complains. Try again when https://flow.org/en/docs/react/refs/
    // is updated to reflect new `React.createRef` API.
    linkRef: {current: null | HTMLAnchorElement} = React.createRef();

    onClick = () => {
        if (this.props.onHitClick) {
            this.props.onHitClick(this.props.hit, this.props.listing);
        }
    };

    render() {
        const {hit, listing} = this.props;
        const {
            bathrooms: {
                full: bathroomsFull,
                half: bathroomsHalf,
                toiletOnly: bathroomsToiletOnly
            },
            bedrooms,
            detailPageUrl,
            listingId,
            priceSummary,
            propertyType,
            sleeps
        } = listing;

        const hasPrice =
            priceSummary ||
            (hit.price && hit.price.value && hit.price.currencyUnits);
        const mapHitRatingClassName = classnames({
            'MapHit__info--priceRating-review': hasPrice,
            'MapHit__info--priceRating-review-noPrice': !hasPrice
        });

        return (
            <div className="MapHit" data-wdio="MapHit">
                <div
                    className="MapHit__carousel content--clickable"
                    data-wdio="MapHit__carousel"
                >
                    <div className="Hit__tripboardButton">
                        <TripBoardButton hit={hit} listing={listing} isMapHit />
                    </div>
                    <HitCarouselContainer
                        detailPageUrl={detailPageUrl}
                        edapAction={MAP_PHOTOCAROUSEL_ADVANCE}
                        images={hit.images}
                        listingId={listingId}
                        onImageClick={this.onClick}
                        totalImageCount={hit.imageCount}
                        simpleCarousel
                    />
                </div>
                <a
                    className={'MapHit__link a--plain-link'}
                    href={listing.detailPageUrl}
                    ref={this.linkRef}
                    target={'_blank'}
                >
                    <div className="MapHit__info">
                        <div className="MapHit__info--details">
                            <BedroomsPropertyTypeElement
                                bedrooms={bedrooms}
                                propertyType={propertyType}
                            />
                            <BathroomsElement
                                bathroomsFull={bathroomsFull}
                                bathroomsHalf={bathroomsHalf}
                                bathroomsToiletOnly={bathroomsToiletOnly}
                            />
                            <SleepsElement value={sleeps} shouldHideDot />
                        </div>
                        <div className="MapHit__info--priceRating">
                            <div className="MapHit__info--priceRating-price">
                                <Price
                                    hit={filterGraphQlAnywhere(
                                        priceFragments.SERPHit,
                                        hit
                                    )}
                                    listing={listing}
                                    useAverage
                                    useShort
                                    fullLocale={hit.fullLocale}
                                />
                            </div>
                            <div className={mapHitRatingClassName}>
                                <Rating
                                    listing={filterGraphQlAnywhere(
                                        ratingFragments.listing,
                                        listing
                                    )}
                                />
                            </div>
                        </div>
                    </div>
                </a>
            </div>
        );
    }
}
